import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Select from "@radix-ui/react-select";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { CloudArrowUpIcon, ArrowDownTrayIcon, XMarkIcon, ArrowPathIcon, UserIcon, ExclamationCircleIcon, ClockIcon, ListBulletIcon, InformationCircleIcon, ChevronDownIcon, PauseIcon, PlayIcon, PauseCircleIcon, NoSymbolIcon } from "@heroicons/react/24/outline";
import { RocketLaunchIcon, BoltIcon, CheckCircleIcon, CheckIcon } from "@heroicons/react/24/solid";
import { apiClient } from "../../lib/axios";
import { useAuthStore } from "../../store/useAuthStore";
import { DataTable, Column, PaginationMeta } from "../../components/ui/DataTable";
import { subscribeToUserChannel, ProgressEvent } from "../../lib/echo";

interface Task {
  id: string;
  company: string;
  status: string;
  progress: number;
  total_records: number;
  processed_records: number;
  created_at: string;
  credits_used: number;
  cached_count?: number;
  depth?: number;
  current_company?: string;
}

interface Lead {
  name: string;
  title: string;
  company_name: string;
  company_domain: string;
  company_type: string;
  company_address: string;
  location: string;
}

type View = "upload" | "progress" | "results";

const DEFAULT_META: PaginationMeta = { total: 0, page: 1, per_page: 20, last_page: 1 };

const DEPTH_OPTIONS = [
  { value: 1, label: "Normal Search", description: "Basic search. Fastest, good results." },
  { value: 2, label: "Deep Search ×1", description: "1 level deeper. Better extraction." },
  { value: 3, label: "Deep Search ×2", description: "2 level deeper. Better extraction." },
  { value: 4, label: "Deep Search ×3", description: "3 levels deeper. Maximum AI extraction." },
  { value: 5, label: "Deep Search ×4", description: "4 levels deeper. Enterprise-grade extraction." },
  { value: 6, label: "Deep Search ×5", description: "5 levels deeper. Best data extraction." },
];

const clean = (v?: string) => (!v || v.toLowerCase() === "unknown" ? "" : v);

const LEAD_COLUMNS: Column<Lead>[] = [
  { key: "name", header: "Name", width: "180px", render: (v) => <span className="font-bold text-slate-900">{clean(v) || "—"}</span> },
  { key: "title", header: "Job Title", width: "160px", render: (v) => clean(v) || "" },
  { key: "company_name", header: "Company", width: "160px", render: (v) => clean(v) || "" },
  {
    key: "company_domain",
    header: "Domain",
    width: "140px",
    render: (v) => {
      const d = clean(v);
      return d ? (
        <a href={`https://${d}`} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline text-xs font-semibold">
          {d}
        </a>
      ) : (
        <span className="text-slate-500">—</span>
      );
    },
  },
  { key: "company_type", header: "Type", width: "130px", render: (v) => clean(v) || "" },
];

function timeAgo(dateStr: string): string {
  const secs = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

const PROCESSING_STATUSES = ["pending", "retrieving", "extracting", "processing", "mapping"];

export default function BulkUpload() {
  useDocumentTitle("Bulk Intelligence", "Batch process records with intelligent de-duplication.");
  const { user } = useAuthStore();

  const [view, setView] = useState<View>("upload");
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [activeMessage, setActiveMessage] = useState("Starting...");

  const [isFileLoaded, setIsFileLoaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState("");
  const [tempFileId, setTempFileId] = useState("");
  const [analysis, setAnalysis] = useState({ total: 0, duplicates: 0, previousMatches: 0, skippedNoCompany: 0, skippedNoLocation: 0, detectedCompanyCol: "", detectedLocationCol: "" });
  const [columnStats, setColumnStats] = useState<Record<string, number>>({});
  const [mapping, setMapping] = useState({ company: "", location: "" });
  const [globalTitles, setGlobalTitles] = useState<string[]>([]);
  const [titleInput, setTitleInput] = useState("");
  const [excludePrevious, setExcludePrevious] = useState(true);
  const [selectedDepth, setSelectedDepth] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [results, setResults] = useState<Lead[]>([]);
  const [resultsMeta, setResultsMeta] = useState<PaginationMeta>(DEFAULT_META);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [cooldownMessage, setCooldownMessage] = useState("");

  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const estCredits = analysis.total * selectedDepth;
  const hasEnoughCredits = (user?.credits ?? 0) >= estCredits;
  const canStart = !!(mapping.company && globalTitles.length > 0);

  useEffect(() => {
    apiClient
      .get("/search/bulk/tasks")
      .then((res) => {
        setRecentTasks((res.data || []).slice(0, 5));
      })
      .catch(() => {});
  }, [view]);

  const loadResults = useCallback(async (taskId: string, page = 1) => {
    setResultsLoading(true);
    try {
      const res = await apiClient.get(`/search/bulk/results/${taskId}`, { params: { page, per_page: 20 } });
      setResults(res.data.data);
      setResultsMeta(res.data.meta);
    } catch {
      toast.error("Failed to load results.");
    } finally {
      setResultsLoading(false);
    }
  }, []);

  const onJobComplete = useCallback(
    (task: Task) => {
      stopPolling();
      setActiveTask(task);
      setActiveMessage("Complete!");
      toast.success(`Batch "${task.company}" finished, ${task.processed_records} of ${task.total_records} companies processed.`);
      loadResults(task.id, 1).then(() => setView("results"));
    },
    [loadResults],
  );

  const startPolling = useCallback(
    (taskId: string) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const res = await apiClient.get<Task>(`/search/bulk/tasks/${taskId}`);
          const t = res.data;
          setActiveTask(t);
          setActiveMessage(`Processing ${t.processed_records} of ${t.total_records} companies…`);
          if (t.status === "completed") onJobComplete(t);
          else if (t.status === "failed") {
            stopPolling();
            toast.error("Batch job failed.");
          } else if (t.status === "paused" || t.status === "cancelled") {
            stopPolling();
          }
        } catch {}
      }, 4000);
    },
    [onJobComplete],
  );

  useEffect(() => {
    if (view !== "progress" || !activeTask || !user) return;
    const taskId = activeTask.id;
    const unsubscribe = subscribeToUserChannel(user.id, (e: ProgressEvent) => {
      if (e.task_id !== taskId) return;
      setActiveMessage(e.message || "Processing…");
      if (e.status === "completed") {
        onJobComplete({ ...activeTask, status: "completed", progress: 100 });
      } else if (e.status === "failed") {
        stopPolling();
        toast.error("Batch job failed.");
        setView("upload");
      } else if (e.status === "paused" || e.status === "cancelled") {
        stopPolling();
        setActiveTask((prev) => (prev ? { ...prev, status: e.status } : prev));
      } else {
        setActiveTask((prev) => {
          if (!prev) return prev;
          const processed = e.leads_found !== null && e.leads_found !== undefined ? e.leads_found : prev.processed_records;
          const pct = prev.total_records > 0 ? Math.min(98, Math.round((processed / prev.total_records) * 100)) : e.progress;
          return { ...prev, status: e.status, progress: pct, processed_records: processed };
        });
      }
    });
    startPolling(taskId);
    return () => {
      unsubscribe();
      stopPolling();
    };
  }, [view, activeTask?.id, user?.id]);

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setIsAnalyzing(true);
    setIsFileLoaded(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await apiClient.post("/search/bulk/analyze", formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setHeaders(res.data.headers);
      setAnalysis({ total: res.data.total_unique, duplicates: res.data.duplicates, previousMatches: res.data.previous_matches, skippedNoCompany: res.data.skipped_no_company ?? 0, skippedNoLocation: res.data.skipped_no_location ?? 0, detectedCompanyCol: res.data.detected_company_col ?? "", detectedLocationCol: res.data.detected_location_col ?? "" });
      setColumnStats(res.data.column_stats ?? {});
      setTempFileId(res.data.temp_file_id);
      const companyCol = res.data.headers.find((h: string) => h.toLowerCase().includes("company"));
      const locationCol = res.data.headers.find((h: string) => h.toLowerCase().includes("location"));
      if (companyCol || locationCol) {
        const newMapping = { company: companyCol || "", location: locationCol || "" };
        setMapping(newMapping);
        if (companyCol) triggerAnalysis(res.data.temp_file_id, newMapping.company, newMapping.location);
      }
      toast.success("File analyzed successfully.");
    } catch {
      toast.error("Failed to analyze file. Check the format.");
      cancelSetup();
    } finally {
      setIsAnalyzing(false);
    }
  };

  const triggerAnalysis = async (fileId: string, companyCol: string, locationCol: string) => {
    if (!fileId || !companyCol) return;
    setIsAnalyzing(true);
    try {
      const res = await apiClient.post("/search/bulk/analyze", { temp_file_id: fileId, company_column: companyCol, location_column: locationCol });
      setAnalysis({ total: res.data.total_unique, duplicates: res.data.duplicates, previousMatches: res.data.previous_matches, skippedNoCompany: res.data.skipped_no_company ?? 0, skippedNoLocation: res.data.skipped_no_location ?? 0, detectedCompanyCol: res.data.detected_company_col ?? "", detectedLocationCol: res.data.detected_location_col ?? "" });
      if (res.data.column_stats) setColumnStats(res.data.column_stats);
    } catch {
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCompanyMappingChange = (v: string) => {
    setMapping((p) => ({ ...p, company: v }));
    triggerAnalysis(tempFileId, v, mapping.location);
  };
  const handleLocationMappingChange = (v: string) => {
    setMapping((p) => ({ ...p, location: v }));
    triggerAnalysis(tempFileId, mapping.company, v);
  };

  const handleAddTitle = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const parts = titleInput.split(",").map((t) => t.trim()).filter(Boolean);
      const toAdd = parts.filter((p) => !globalTitles.includes(p));
      const remaining = 5 - globalTitles.length;
      if (toAdd.length > 0 && remaining > 0) {
        setGlobalTitles((p) => [...p, ...toAdd.slice(0, remaining)]);
        setTitleInput("");
      }
    }
  };

  const handleTitleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.includes(",")) {
      const parts = val.split(",").map((t) => t.trim());
      const endsWithComma = val.trimEnd().endsWith(",");
      const completeParts = endsWithComma ? parts.filter(Boolean) : parts.slice(0, -1).filter(Boolean);
      const inProgress = endsWithComma ? "" : (parts[parts.length - 1] ?? "");
      if (completeParts.length > 0) {
        const remaining = 5 - globalTitles.length;
        const toAdd = completeParts.filter((p) => !globalTitles.includes(p));
        setGlobalTitles((p) => [...p, ...toAdd.slice(0, remaining)]);
      }
      setTitleInput(inProgress);
    } else {
      setTitleInput(val);
    }
  };

  const cancelSetup = () => {
    setIsFileLoaded(false);
    setHeaders([]);
    setGlobalTitles([]);
    setMapping({ company: "", location: "" });
    setTempFileId("");
    setAnalysis({ total: 0, duplicates: 0, previousMatches: 0, skippedNoCompany: 0, skippedNoLocation: 0, detectedCompanyCol: "", detectedLocationCol: "" });
    setColumnStats({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const startBatch = async () => {
    if (!mapping.company) {
      toast.error("Company column must be mapped.");
      return;
    }
    if (globalTitles.length === 0) {
      toast.error("Add at least one job title.");
      return;
    }
    setIsUploading(true);
    try {
      const res = await apiClient.post("/search/bulk/start", {
        temp_file_id: tempFileId,
        file_name: fileName.replace(/\.csv$/i, ""),
        mapping,
        titles: globalTitles,
        depth: selectedDepth,
        exclude_previous: excludePrevious,
        total_unique: analysis.total,
      });
      toast.success("Batch started!");
      cancelSetup();
      setActiveTask({ id: res.data.task_id, company: fileName.replace(".csv", ""), status: "pending", progress: 0, total_records: analysis.total, processed_records: 0, created_at: new Date().toISOString(), credits_used: 0 });
      setActiveMessage("Starting batch…");
      setView("progress");
    } catch {
      toast.error("Failed to start batch.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async () => {
    if (!activeTask) return;
    try {
      const res = await apiClient.get(`/search/export/download/${activeTask.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const ts  = new Date().toISOString().slice(0, 19).replace(/[-T:]/g, (c) => (c === 'T' ? '_' : c));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeTask.company || "results"}_${ts}_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Download started!");
    } catch {
      toast.error("Download failed.");
    }
  };

  const resetToUpload = () => {
    stopPolling();
    setView("upload");
    setActiveTask(null);
    setResults([]);
    setResultsMeta(DEFAULT_META);
  };

  const [jobActionLoading, setJobActionLoading] = useState<"pause" | "resume" | "cancel" | null>(null);

  const handlePauseJob = async () => {
    if (!activeTask) return;
    setJobActionLoading("pause");
    try {
      await apiClient.post(`/search/bulk/tasks/${activeTask.id}/pause`);
      stopPolling();
      setActiveTask((prev) => (prev ? { ...prev, status: "paused" } : prev));
      toast.success("Job paused.");
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Failed to pause job.");
    } finally {
      setJobActionLoading(null);
    }
  };

  const handleResumeJob = async () => {
    if (!activeTask) return;
    setJobActionLoading("resume");
    try {
      await apiClient.post(`/search/bulk/tasks/${activeTask.id}/resume`);
      setActiveTask((prev) => (prev ? { ...prev, status: "processing" } : prev));
      setActiveMessage("Resuming job…");
      startPolling(activeTask.id);
      toast.success("Job resumed.");
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Failed to resume job.");
    } finally {
      setJobActionLoading(null);
    }
  };

  const handleCancelJob = async () => {
    if (!activeTask) return;
    if (!window.confirm("Cancel this batch? Companies already processed stay available, but the rest will be skipped.")) return;
    setJobActionLoading("cancel");
    try {
      await apiClient.post(`/search/bulk/tasks/${activeTask.id}/cancel`);
      stopPolling();
      setActiveTask((prev) => (prev ? { ...prev, status: "cancelled" } : prev));
      toast.success("Job cancelled.");
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Failed to cancel job.");
    } finally {
      setJobActionLoading(null);
    }
  };

  const downloadTemplate = () => {
    const csv = "Company Name,Address\nLucky Restaurant,San Francisco, California, 94102\nCafe Noir,Austin, Texas, 78701";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_upload_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── RESULTS VIEW ──────────────────────────────────────────────────────────
  if (view === "results" && activeTask) {
    return (
      <div className="h-full flex flex-col bg-slate-50">
        <style>{`@keyframes slide{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}.animate-slide{animation:slide 1.5s infinite linear}`}</style>
        <div className="border-b border-slate-200 bg-white px-4 sm:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={resetToUpload} className="flex items-center gap-1.5 text-sm font-bold text-slate-400 hover:text-slate-800 transition-colors">
              ← New Batch
            </button>
            <span className="text-slate-400">|</span>
            <span className="text-sm font-bold text-slate-400">Results</span>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Link to="/dashboard/search_history" className="flex items-center gap-2 bg-white border border-slate-200 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold text-slate-600 hover:bg-slate-50 shadow-sm">
              <ListBulletIcon className="w-4 h-4" /> <span className="hidden sm:inline">History</span>
            </Link>
            <button onClick={handleDownload} className="flex items-center gap-2 bg-brand-500 text-slate-900 px-3 sm:px-5 py-2 rounded-xl text-xs sm:text-sm font-bold hover:bg-brand-600 [box-shadow:0_4px_16px_rgba(0,84,214,0.35)]">
              <ArrowDownTrayIcon className="w-4 h-4" /> <span className="hidden sm:inline">Export CSV</span>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-6 sm:py-8 flex flex-col gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="rounded-2xl p-6 bg-brand-600 border border-brand-700 shadow-sm">
                <p className="text-[11px] font-bold text-white uppercase tracking-wider mb-1">Batch</p>
                <p className="text-xl font-extrabold text-slate-900 truncate">{activeTask.company}</p>
              </div>
              <div className="rounded-2xl p-6 bg-white border border-slate-200 shadow-sm">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Leads Found</p>
                <p className="text-2xl font-extrabold text-slate-900">{resultsMeta.total.toLocaleString()}</p>
              </div>
              <div className="rounded-2xl p-6 bg-white border border-slate-200 shadow-sm">
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Companies</p>
                <p className="text-2xl font-extrabold text-slate-900">{(activeTask.processed_records ?? 0).toLocaleString()}</p>
              </div>
            </div>
            {(activeTask.cached_count ?? 0) > 0 && (
              <div className="flex items-start gap-2.5 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
                <BoltIcon className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-[12px] text-blue-700 leading-relaxed">
                  <span className="font-bold">{(activeTask.cached_count ?? 0).toLocaleString()} rows were skipped</span> because they were run in the last 30 days (same company &amp; address), reused free, not charged.
                </p>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <DataTable<Lead> columns={LEAD_COLUMNS} data={results} meta={resultsMeta} onPageChange={(p) => loadResults(activeTask.id, p)} isLoading={resultsLoading} emptyMessage="No leads extracted." />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN LAYOUT ───────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col bg-slate-50">
      <style>{`
        @keyframes slide{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}.animate-slide{animation:slide 1.5s infinite linear}
        @keyframes fadeIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}.fade-in{animation:fadeIn 0.22s ease-out both}
        @keyframes selectIn{from{opacity:0;transform:translateY(-4px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}
        [data-radix-select-content][data-state='open']{animation:selectIn 140ms ease-out}
      `}</style>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1060px] mx-auto px-4 sm:px-8 py-4 sm:py-6">
          {/* Page header */}
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">Bulk Intelligence</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Batch process records with intelligent de-duplication.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
            {/* ── LEFT COLUMN ── */}
            <div className="min-w-0 space-y-4">
              {/* PROGRESS VIEW */}
              {view === "progress" && activeTask && (
                <div className="fade-in space-y-4">
                  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
                    <div className="flex items-center gap-4 mb-6">
                      {activeTask.status === "failed" || activeTask.status === "cancelled"
                        ? <ExclamationCircleIcon className="w-10 h-10 text-red-400 shrink-0" />
                        : activeTask.status === "paused"
                          ? <PauseCircleIcon className="w-10 h-10 text-amber-400 shrink-0" />
                          : <RocketLaunchIcon className="w-10 h-10 text-brand-500 animate-pulse shrink-0" />}
                      <div>
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                          {activeTask.status === "failed" ? "Job Failed" : activeTask.status === "cancelled" ? "Cancelled" : activeTask.status === "paused" ? "Paused" : "Processing"}
                        </p>
                        <h2 className="text-xl font-extrabold text-slate-900">{activeTask.company}</h2>
                      </div>
                    </div>
                    {activeTask.status !== "failed" && activeTask.status !== "cancelled" && (
                      <>
                        <p className="text-sm font-semibold text-slate-600 mb-2">
                          {activeTask.status === "paused"
                            ? <>This batch is paused at {activeTask.processed_records.toLocaleString()} of {activeTask.total_records.toLocaleString()} {activeTask.total_records === 1 ? "company" : "companies"}. Resume to continue.</>
                            : <>We're enriching your {activeTask.total_records.toLocaleString()} {activeTask.total_records === 1 ? "company" : "companies"}. This runs entirely in the background.</>}
                        </p>
                        {activeTask.status !== "paused" && activeTask.current_company && (
                          <p className="text-xs text-slate-500 mb-3 italic">
                            Currently processing: <span className="font-semibold text-slate-700">{activeTask.current_company}</span>
                          </p>
                        )}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-slate-500">
                              Progress: {activeTask.processed_records} of {activeTask.total_records}
                            </span>
                            <span className={`text-xs font-bold ${activeTask.status === "paused" ? "text-amber-500" : "text-brand-600"}`}>{activeTask.progress}%</span>
                          </div>
                          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-300 ${activeTask.status === "paused" ? "bg-amber-400" : "bg-brand-500"}`} style={{ width: `${activeTask.progress}%` }} />
                          </div>
                        </div>

                        {/* Job controls */}
                        <div className="flex flex-wrap items-center gap-2.5">
                          {activeTask.status === "paused" ? (
                            <button onClick={handleResumeJob} disabled={jobActionLoading !== null} className="flex items-center gap-1.5 bg-brand-500 text-slate-900 px-4 py-2 rounded-xl text-sm font-bold hover:bg-brand-600 transition-colors disabled:opacity-50">
                              {jobActionLoading === "resume" ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlayIcon className="w-4 h-4" />} Resume
                            </button>
                          ) : (
                            <button onClick={handlePauseJob} disabled={jobActionLoading !== null} className="flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors disabled:opacity-50">
                              {jobActionLoading === "pause" ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PauseIcon className="w-4 h-4" />} Pause
                            </button>
                          )}
                          <button onClick={handleCancelJob} disabled={jobActionLoading !== null} className="flex items-center gap-1.5 bg-white border border-red-200 text-red-600 px-4 py-2 rounded-xl text-sm font-bold hover:bg-red-50 transition-colors disabled:opacity-50">
                            {jobActionLoading === "cancel" ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <XMarkIcon className="w-4 h-4" />} Cancel
                          </button>
                        </div>
                        <p className="text-xs text-slate-400 mt-3">You'll receive a notification when the batch is complete, feel free to navigate away.</p>
                      </>
                    )}
                    {activeTask.status === "cancelled" && (
                      <>
                        <p className="text-sm font-semibold text-slate-600 mb-4">
                          This batch was cancelled after {activeTask.processed_records.toLocaleString()} of {activeTask.total_records.toLocaleString()} companies. Processed results are still available in your history.
                        </p>
                        <div className="flex flex-wrap gap-2.5">
                          <Link to="/dashboard/search_history" className="bg-white border border-slate-200 text-slate-700 px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-50">
                            View History
                          </Link>
                          <button onClick={resetToUpload} className="bg-brand-500 text-slate-900 px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-600">
                            New Batch
                          </button>
                        </div>
                      </>
                    )}
                    {activeTask.status === "failed" && (
                      <button onClick={resetToUpload} className="mt-4 bg-brand-500 text-slate-900 px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-600">
                        Try Again
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex items-center gap-3">
                      <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center shrink-0">
                        <ClockIcon className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Started</p>
                        <p className="text-sm font-bold text-slate-900">{new Date(activeTask.created_at).toLocaleTimeString()}</p>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex items-center gap-3">
                      <div className="w-9 h-9 bg-brand-50 rounded-xl flex items-center justify-center shrink-0">
                        <BoltIcon className="w-4 h-4 text-brand-500" />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Companies</p>
                        <p className="text-sm font-bold text-slate-900">{activeTask.total_records.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* UPLOAD / MAPPING VIEW */}
              {view === "upload" && (
                <>
                  {/* Brand vs. local-shop warning — shown before upload & during mapping */}
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 sm:px-5 py-4 mb-5 fade-in">
                    <ExclamationCircleIcon className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[13px] font-extrabold text-red-700 mb-1">Read this before you upload</p>
                      <p className="text-[12.5px] text-red-700/90 leading-relaxed">
                        We search using the company name and the address. If the name is a big brand or franchise, you get the owner of the whole brand, not the owner of that shop. You get the same person on every row, and you pay for every row. To find the local owner, your file needs the real shop name, not the brand name. <span className="font-bold">No refunds either way.</span>
                      </p>
                    </div>
                  </div>

                  {/* Drop zone — shown when no file */}
                  {!isFileLoaded && (
                    <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-slate-200 hover:border-brand-400 rounded-2xl flex flex-col items-center justify-center text-center cursor-pointer transition-colors group fade-in py-8 sm:py-14 px-4 sm:px-8 bg-white">
                      <input type="file" accept=".csv" className="hidden" ref={fileInputRef} onChange={handleFileImport} />
                      <div className="w-16 sm:w-20 h-16 sm:h-20 bg-brand-50 rounded-full flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-brand-100 transition-colors">
                        <CloudArrowUpIcon className="w-7 sm:w-9 h-7 sm:h-9 text-brand-500" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 mb-2">Import your data</h3>
                      <p className="text-xs sm:text-sm text-slate-500 mb-3 max-w-sm leading-relaxed">
                        Drag and drop your CSV file here. <span className="text-red-500 font-semibold">Company Name and Address are required.</span>
                      </p>
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2.5 mb-6 sm:mb-8 max-w-sm text-left" onClick={(e) => e.stopPropagation()}>
                        <InformationCircleIcon className="w-4 h-4 text-amber-500 shrink-0 mt-px" />
                        <p className="text-xs text-amber-700 leading-relaxed">After uploading, you'll need to add <span className="font-semibold">at least one job title</span> (e.g. CEO, Founder, CTO) to start the search.</p>
                      </div>
                      <div className="flex flex-col sm:flex-row items-center gap-3 mb-6 sm:mb-8" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => fileInputRef.current?.click()} className="bg-brand-500 text-slate-900 px-6 py-2.5 rounded-xl text-xs sm:text-sm font-bold hover:bg-brand-600 transition-colors w-full sm:w-auto">
                          Browse Files
                        </button>
                        {/* <button
                          onClick={downloadTemplate}
                          className="bg-white border border-slate-200 text-slate-700 px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors"
                        >
                          Download Template
                        </button> */}
                      </div>
                      <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-[10px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                        <span>CSV Format</span>
                        <div className="hidden sm:block w-px h-4 bg-slate-200" />
                        <span>Max 20MB</span>
                        <div className="hidden sm:block w-px h-4 bg-slate-200" />
                        <span>Duplicates Auto-Cleaned</span>
                      </div>
                    </div>
                  )}

                  {/* Configuration card — shown when file is loaded */}
                  {isFileLoaded && (
                    <div className="fade-in space-y-4">
                      {/* Config header */}
                      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                        <div className="px-4 sm:px-5 py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 border-b border-slate-100">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-2 h-2 rounded-full shrink-0 ${isAnalyzing ? "bg-amber-400 animate-pulse" : "bg-emerald-500"}`} />
                            <span className="text-[10px] sm:text-[11px] font-extrabold text-slate-500 uppercase tracking-widest truncate">
                              Configuration: <span className="text-slate-800 truncate">{fileName}</span>
                            </span>
                          </div>
                          <button onClick={cancelSetup} className="text-[10px] sm:text-[11px] font-extrabold text-slate-400 hover:text-red-500 uppercase tracking-widest transition-colors text-right">
                            Cancel
                          </button>
                        </div>

                        {/* Analysis stats */}
                        {!isAnalyzing && (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 sm:divide-x sm:divide-slate-100">
                              {/* Duplicates Cleaned card */}
                              <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
                                <p className="text-[10px] font-extrabold text-amber-600 uppercase tracking-widest mb-1">Duplicates Cleaned</p>
                                <p className="text-2xl sm:text-3xl font-extrabold text-amber-700">{analysis.duplicates.toLocaleString()}</p>
                                <p className="text-xs text-amber-500/80 mt-1">rows removed from input</p>
                              </div>
                              {/* Previously Enriched card */}
                              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                                <div className="flex items-center gap-1.5 group relative">
                                  <p className="text-[10px] font-extrabold text-emerald-600 uppercase tracking-widest mb-1">Previously Enriched</p>
                                  <InformationCircleIcon className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                  <div className="hidden group-hover:block absolute bottom-full left-0 mb-2 w-48 bg-slate-900 text-white text-[11px] rounded-lg p-2.5 pointer-events-none z-10">
                                    <p>Companies at these locations that have been searched before.</p>
                                    <div className="absolute bottom-0 left-4 translate-y-full w-2 h-2 bg-slate-900 transform rotate-45" />
                                  </div>
                                </div>
                                <p className="text-2xl sm:text-3xl font-extrabold text-emerald-700">{analysis.previousMatches.toLocaleString()}</p>
                                <div className="flex items-center justify-between mt-2 flex-col sm:flex-row gap-2 sm:gap-0">
                                  <p className="text-xs text-emerald-500/80">previously enriched (30d)</p>
                                  {analysis.previousMatches > 0 && (
                                    <label className="flex items-center gap-1.5 cursor-pointer">
                                      <input type="checkbox" checked={excludePrevious} onChange={(e) => setExcludePrevious(e.target.checked)} className="w-3.5 h-3.5 rounded text-brand-600 border-slate-300" />
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Skip</span>
                                    </label>
                                  )}
                                </div>
                                {analysis.previousMatches > 0 && <p className="text-[11px] text-emerald-600 mt-3 pt-3 border-t border-emerald-200/50 leading-relaxed">Same companies searched in last 30 days will be free if results found. Check "Skip" to exclude them.</p>}
                              </div>
                            </div>

                            {/* Rows skipped — company name or location missing */}
                            {(analysis.skippedNoCompany > 0 || analysis.skippedNoLocation > 0) && (() => {
                              const total = analysis.skippedNoCompany + analysis.skippedNoLocation;
                              return (
                                <div className="mx-4 sm:mx-5 mb-4 rounded-xl bg-red-50 border border-red-100 p-3.5">
                                  <div className="flex items-center gap-2.5">
                                    <span className="min-w-[28px] h-[28px] rounded-full bg-red-100 text-red-700 text-[11px] font-black flex items-center justify-center shrink-0 px-1">{total}</span>
                                    <div>
                                      <p className="text-xs font-bold text-red-700">{total === 1 ? "1 row skipped" : `${total} rows skipped`},  missing company name or location</p>
                                      {(analysis.skippedNoCompany > 0 && analysis.skippedNoLocation > 0) && (
                                        <p className="text-[10px] text-red-500 mt-0.5">
                                          {analysis.skippedNoCompany} missing company{analysis.detectedCompanyCol ? ` (${analysis.detectedCompanyCol})` : ""} · {analysis.skippedNoLocation} missing location{analysis.detectedLocationCol ? ` (${analysis.detectedLocationCol})` : ""}
                                        </p>
                                      )}
                                      {(analysis.skippedNoCompany > 0 && analysis.skippedNoLocation === 0) && (
                                        <p className="text-[10px] text-red-500 mt-0.5">No value in {analysis.detectedCompanyCol ? `"${analysis.detectedCompanyCol}"` : "company"} column</p>
                                      )}
                                      {(analysis.skippedNoLocation > 0 && analysis.skippedNoCompany === 0) && (
                                        <p className="text-[10px] text-red-500 mt-0.5">No value in {analysis.detectedLocationCol ? `"${analysis.detectedLocationCol}"` : "location"} column</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </>
                        )}

                        {isAnalyzing && (
                          <div className="px-5 py-6 flex items-center gap-3">
                            <ArrowPathIcon className="w-4 h-4 text-brand-400 animate-spin shrink-0" />
                            <span className="text-sm text-slate-500 font-medium">Analyzing file…</span>
                          </div>
                        )}
                      </div>

                      {/* Column mapping */}
                      <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden ${isAnalyzing ? "opacity-50 pointer-events-none" : ""}`}>
                        <div className="px-4 sm:px-5 py-3.5 border-b border-slate-100">
                          <span className="text-[10px] sm:text-[11px] font-extrabold text-slate-500 uppercase tracking-widest">Column Mapping</span>
                        </div>
                        <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">
                              Company Name <span className="text-red-400">*</span>
                            </label>
                            <MappingSelect value={mapping.company} onValueChange={handleCompanyMappingChange} options={headers} columnStats={columnStats} placeholder="Select column" />
                          </div>
                          <div>
                            <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Address Column</label>
                            <MappingSelect value={mapping.location} onValueChange={handleLocationMappingChange} options={headers} columnStats={columnStats} placeholder="Select column" />
                          </div>
                        </div>
                      </div>

                      {/* Search roles */}
                      <div className={`bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden ${isAnalyzing ? "opacity-50 pointer-events-none" : ""}`}>
                        <div className="px-4 sm:px-5 py-3.5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                          <div className="flex items-center gap-2">
                            <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-[10px] sm:text-[11px] font-extrabold text-slate-500 uppercase tracking-widest">Search Roles ({globalTitles.length}/5)</span>
                            <span className="text-red-400 text-[11px] font-bold">*</span>
                          </div>
                          {globalTitles.length >= 5 && <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider text-right">Max reached</span>}
                        </div>
                        <div className="p-4 sm:p-5">
                          {globalTitles.length === 0 && (
                            <div className="flex items-start gap-2 bg-brand-50 border border-brand-100 rounded-xl px-3.5 py-2.5 mb-3">
                              <InformationCircleIcon className="w-4 h-4 text-brand-500 shrink-0 mt-px" />
                              <p className="text-xs text-brand-700 leading-relaxed">Add at least one job title to enable search, e.g. <span className="font-semibold">CEO</span>, <span className="font-semibold">Founder</span>, <span className="font-semibold">CTO</span>.</p>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
                            {globalTitles.map((t, i) => (
                              <span key={i} className="inline-flex items-center gap-1 bg-brand-200 text-slate-800 border border-brand-400 px-2.5 py-1 rounded-full text-sm font-semibold">
                                {t}
                                <button onClick={() => setGlobalTitles(globalTitles.filter((_, idx) => idx !== i))}>
                                  <XMarkIcon className="w-3 h-3 hover:text-red-500" />
                                </button>
                              </span>
                            ))}
                          </div>
                          {globalTitles.length < 5 && <input type="text" placeholder='Type roles and press Enter, e.g. "CEO, Founder, CTO"' className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-500 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-500/20 transition-colors" value={titleInput} onChange={handleTitleInputChange} onKeyDown={handleAddTitle} />}
                          {globalTitles.length >= 5 && <div className="text-[12px] text-slate-500 italic">Maximum 5 job titles allowed. Remove one to add another.</div>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Instructions (no file) */}
                  {!isFileLoaded && (
                    <div className="rounded-2xl border border-slate-100 overflow-hidden fade-in">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                        {[
                          { n: "01", title: "Prepare your CSV", body: "Include Company Name and Address (city, state, zipcode). Many businesses share the same name across multiple locations, address ensures we find the right one." },
                          { n: "02", title: "Map columns & add roles", body: "Select which CSV columns map to Company and Address. Add up to 5 job titles you want to find." },
                          { n: "03", title: "Run in the background", body: "Once started, you can leave the page. Results arrive via email when complete. We charge per company, not per person." },
                        ].map((s, i) => (
                          <div key={i} className="p-4 sm:p-5 bg-white">
                            <span className="text-[10px] font-bold text-brand-700 uppercase tracking-wider block mb-2 sm:mb-3">Step {s.n}</span>
                            <p className="text-xs sm:text-sm font-bold text-slate-800 mb-1.5">{s.title}</p>
                            <p className="text-[12px] sm:text-xs text-slate-500 leading-relaxed">{s.body}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── RIGHT COLUMN ── */}
            <div className="space-y-4">
              {/* Search Intensity card */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-4 sm:px-5 py-3.5 border-b border-slate-100">
                  <span className="text-[10px] sm:text-[11px] font-extrabold text-slate-500 uppercase tracking-widest">Search Intensity</span>
                </div>
                <div className="p-4 sm:p-5">
                  <Select.Root value={String(selectedDepth)} onValueChange={(v) => setSelectedDepth(Number(v))}>
                    <Select.Trigger className="inline-flex items-center justify-between w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-slate-900 outline-none hover:border-slate-300 focus:ring-2 focus:ring-brand-500/20 transition-colors bg-white">
                      <Select.Value />
                      <Select.Icon>
                        <ChevronDownIcon className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
                      </Select.Icon>
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content data-radix-select-content="" className="overflow-hidden bg-white border border-slate-200 rounded-xl shadow-xl z-[200]">
                        <Select.Viewport className="p-1">
                          {DEPTH_OPTIONS.map((opt) => (
                            <Select.Item key={opt.value} value={String(opt.value)} className="flex items-center px-3 py-2.5 rounded-lg cursor-pointer outline-none hover:bg-brand-50 hover:text-brand-800 data-[state=checked]:bg-brand-500 data-[state=checked]:text-slate-900 transition-colors">
                              <Select.ItemText>
                                <span className="text-sm font-bold block">{opt.label}</span>
                              </Select.ItemText>
                            </Select.Item>
                          ))}
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>

                  {/* Description of selected depth */}
                  <p className="text-xs text-slate-400 mt-2.5 leading-relaxed">{DEPTH_OPTIONS.find((o) => o.value === selectedDepth)?.description}</p>

                  {/* Pro Tip */}
                  <div className="mt-4 rounded-xl bg-blue-50 border border-blue-100 p-3.5 flex gap-2.5">
                    <InformationCircleIcon className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[11px] font-extrabold text-blue-700 uppercase tracking-wider mb-0.5">Pro Tip</p>
                      <p className="text-xs text-blue-600 leading-relaxed">Deep Search ×1 or higher may find more decision makers.</p>
                    </div>
                  </div>

                  {/* Pricing Model Info */}
                  <div className="mt-3 rounded-xl bg-brand-50 border border-brand-100 p-3.5 flex gap-2.5">
                    <div className="flex flex-col gap-1">
                      <p className="text-[11px] font-extrabold text-brand-700 uppercase tracking-wider">Per Company • Live Search</p>
                      <p className="text-xs text-brand-900 leading-relaxed">We charge per company searched, not per person found. Every search is live and real-time.</p>
                    </div>
                  </div>
                </div>

                {/* Stats + CTA */}
                <div className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-3">
                      <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">Unique Companies</p>
                      <p className="text-lg sm:text-xl font-extrabold text-slate-900">{analysis.total > 0 ? analysis.total.toLocaleString() : "—"}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-3">
                      <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-1">Total Estimated Credits</p>
                      <p className="text-lg sm:text-xl font-extrabold text-slate-900">{analysis.total > 0 ? estCredits.toLocaleString() : "—"}</p>
                    </div>
                  </div>

                  {isFileLoaded && !isAnalyzing && !canStart && <p className="text-[10px] font-bold text-slate-400 text-center">Map company column and add at least one role</p>}
                  {isFileLoaded && canStart && !hasEnoughCredits && <p className="text-[10px] font-bold text-red-500 text-center">Insufficient credits, need {estCredits.toLocaleString()}</p>}

                  {view === "upload" && (
                    <button onClick={isFileLoaded ? startBatch : () => fileInputRef.current?.click()} disabled={isFileLoaded ? isUploading || isAnalyzing || !canStart || !hasEnoughCredits : false} className="w-full bg-brand-500 text-slate-900 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm font-extrabold flex items-center justify-center gap-2 hover:bg-brand-600 transition-colors [box-shadow:0_4px_16px_rgba(0,84,214,0.35)] disabled:opacity-40 disabled:shadow-none">
                      {isUploading ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 animate-spin" /> <span className="hidden sm:inline">Starting…</span>
                          <span className="sm:hidden">…</span>
                        </>
                      ) : isFileLoaded ? (
                        <>
                          <BoltIcon className="w-4 h-4" /> <span className="hidden sm:inline">Start Intelligence Batch</span>
                          <span className="sm:hidden">Start Batch</span>
                        </>
                      ) : (
                        <>
                          <CloudArrowUpIcon className="w-4 h-4" /> <span className="hidden sm:inline">Upload File</span>
                          <span className="sm:hidden">Upload</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Recent Batches card */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-4 sm:px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-2">
                  <span className="text-[10px] sm:text-[11px] font-extrabold text-slate-500 uppercase tracking-widest">Recent Batches</span>
                  <Link to="/dashboard/search_history" className="text-[10px] sm:text-xs font-bold text-brand-600 hover:text-brand-800 whitespace-nowrap">
                    View All
                  </Link>
                </div>

                <div className="p-3 sm:p-3">
                  {recentTasks.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-6">No recent batches.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {recentTasks.map((task) => {
                        const isProcessing = PROCESSING_STATUSES.includes(task.status);
                        return (
                          <div key={task.id} className={`rounded-xl border p-3.5 ${isProcessing ? "bg-slate-900 border-slate-700" : "bg-slate-50 border-slate-100"}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-bold truncate max-w-[140px] ${isProcessing ? "text-white" : "text-slate-900"}`}>{task.company}</span>
                              {task.status === "completed" ? <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0" /> : task.status === "failed" ? <ExclamationCircleIcon className="w-4 h-4 text-red-400 shrink-0" /> : task.status === "paused" ? <PauseCircleIcon className="w-4 h-4 text-amber-400 shrink-0" /> : task.status === "cancelled" ? <NoSymbolIcon className="w-4 h-4 text-slate-400 shrink-0" /> : <ArrowPathIcon className="w-4 h-4 text-brand-400 shrink-0 animate-spin" />}
                            </div>
                            <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isProcessing ? "text-slate-300" : "text-slate-400"}`}>{task.status === "completed" ? `Completed · ${timeAgo(task.created_at)}` : task.status === "failed" ? "Failed" : task.status === "paused" ? "Paused" : task.status === "cancelled" ? "Cancelled" : `Processing ${task.total_records.toLocaleString()} records`}</p>
                            {isProcessing && (
                              <div className="w-full h-1 bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full w-1/3 bg-brand-500 rounded-full animate-slide" />
                              </div>
                            )}
                            {task.status === "completed" && (
                              <Link to={`/dashboard/search_history/${task.id}`} className="text-[10px] font-bold text-brand-600 uppercase tracking-wider hover:text-brand-800">
                                View Results →
                              </Link>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MappingSelect({ value, onValueChange, options, columnStats, placeholder }: { value: string; onValueChange: (v: string) => void; options: string[]; columnStats: Record<string, number>; placeholder: string }) {
  return (
    <Select.Root value={value} onValueChange={onValueChange}>
      <Select.Trigger className="inline-flex items-center justify-between w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none hover:border-slate-300 focus:ring-2 focus:ring-brand-500/20 transition-colors bg-white">
        <Select.Value placeholder={<span className="text-slate-400 font-normal text-sm">{placeholder}</span>} />
        <Select.Icon>
          <ChevronDownIcon className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content data-radix-select-content="" className="overflow-hidden bg-white border border-slate-200 rounded-xl shadow-xl z-[200]">
          <Select.Viewport className="p-1">
            {options.filter((opt) => opt !== "").map((opt) => {
              const count = columnStats[opt];
              return (
                <Select.Item key={opt} value={opt} className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 cursor-pointer outline-none hover:bg-brand-50 hover:text-brand-700 data-[state=checked]:bg-brand-500 data-[state=checked]:text-slate-900 transition-colors gap-3">
                  <Select.ItemText>{opt}</Select.ItemText>
                  {count !== undefined && (
                    <span className="text-[10px] font-bold text-slate-400 shrink-0">{count} rows</span>
                  )}
                </Select.Item>
              );
            })}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
